import { triggerLogout } from "./authHandler";

// let inactivityTimer = null;
// const INACTIVITY_LIMIT = 30 * 60 * 1000;

// const resetTimer = () => {
//   if (inactivityTimer) clearTimeout(inactivityTimer);
//   inactivityTimer = setTimeout(triggerLogout, INACTIVITY_LIMIT);
// };

// API Base URL
const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:8000/api";

// Get token from localStorage
const getToken = () => {
  return localStorage.getItem("authToken");
};

// Generic API call function
export const apiCall = async (
  endpoint,
  method = "GET",
  data = null,
  isFormData = false,
  expectBlob = false
) => {
  // resetTimer();

  const url = `${API_BASE_URL}${endpoint}`;
  const token = getToken();

  const headers = {};

  if (!isFormData && !expectBlob) {
    headers["Content-Type"] = "application/json";
  }

  if (token) {
    headers["Authorization"] = `Token ${token}`;
  }

  const options = {
    method,
    headers: headers,
  };

  if (data) {
    if (isFormData) {
      options.body = data;
    } else {
      options.body = JSON.stringify(data);
    }
  }

  try {
    const response = await fetch(url, options);

    if (response.status === 401) {
      triggerLogout();
      throw new Error("Session expired");
    }

    if (expectBlob) {
      if (!response.ok) {
        throw new Error("Failed to download file");
      }
      return await response.blob();
    }

    const responseData = await response.json();

    if (!response.ok) {
      throw new Error(responseData.error || "API request failed");
    }

    return responseData;
  } catch (error) {
    throw error;
  }
};

// Auth API functions
export const authAPI = {
  register: (name, email, password, passwordConfirm) =>
    apiCall("/auth/register/", "POST", {
      name,
      email,
      password,
      password_confirm: passwordConfirm,
    }),

  login: (email, password) =>
    apiCall("/auth/login/", "POST", { email, password }),

  logout: () => apiCall("/auth/logout/", "POST"),

  getUserDetails: () => apiCall("/auth/user/", "GET"),
};

// Client API functions
export const clientAPI = {
  getClients: (search = "", ordering = "name") =>
    apiCall(
      `/clients/?${new URLSearchParams({ search, ordering }).toString()}`,
      "GET"
    ),

  getClientDetail: (clientId) => apiCall(`/clients/${clientId}/`, "GET"),

  createClient: (clientData) => apiCall("/clients/", "POST", clientData),

  updateClient: (clientId, clientData) =>
    apiCall(`/clients/${clientId}/`, "PUT", clientData),

  deleteClient: (clientId) => apiCall(`/clients/${clientId}/`, "DELETE"),
};

// Event API functions
export const eventAPI = {
  getEvents: (search = "", status = "", category = "") =>
    apiCall(
      `/events/?search=${search}&status=${status}&category=${category}`,
      "GET"
    ),

  getEventDetail: (eventId) => apiCall(`/events/${eventId}/`, "GET"),

  getEventPDF: (eventId) =>
    apiCall(
      `/events/download-invoice-pdf/${eventId}`,
      "GET",
      null,
      false,
      true
    ),

  createEvent: (eventData) => apiCall("/events/", "POST", eventData, true),

  sendEventPDF: (eventData) =>
    apiCall("/events/send-to-client/", "POST", eventData, true),

  updateEvent: (eventId, eventData) =>
    apiCall(`/events/${eventId}/`, "PUT", eventData, true),

  deleteEvent: (eventId) => apiCall(`/events/${eventId}/`, "DELETE"),

  addEventAddons: (eventId, addonsData) =>
    apiCall(`/events/${eventId}/addons/`, "POST", { addons: addonsData }),

  updateEventAddon: (eventId, addonId, addonData) =>
    apiCall(`/events/${eventId}/addons/${addonId}/`, "PUT", addonData),

  getPackages: () => apiCall("/events/packages/", "GET"),

  getClientEvents: (clientId) =>
    apiCall(`/events/client/${clientId}/events/`, "GET"),

  getCalendarEvents: () => apiCall("/events/calendar/", "GET"),

  getPayments: (eventId) => apiCall(`/events/${eventId}/payments/`, "GET"),

  addPayment: (eventId, paymentData) =>
    apiCall(`/events/${eventId}/payments/`, "POST", paymentData),

  updatePayment: (eventId, paymentId, paymentData) =>
    apiCall(`/events/${eventId}/payments/${paymentId}/`, "PUT", paymentData),

  deletePayment: (eventId, paymentId) =>
    apiCall(`/events/${eventId}/payments/${paymentId}/`, "DELETE"),
};

// Dashboard API functions
export const dashboardAPI = {
  getServerDate: () => apiCall("/dashboard/server-date/", "GET"),

  getStats: ({ period = "this_month", start = "", end = "" } = {}) => {
    const params = new URLSearchParams({ period });
    if (period === "custom") {
      if (start) params.append("start", start);
      if (end) params.append("end", end);
    }
    return apiCall(`/dashboard/stats/?${params.toString()}`, "GET");
  },

  getRecentEvents: (limit = 10) =>
    apiCall(`/dashboard/recent-events/?limit=${limit}`, "GET"),

  getUpcomingEvents: (limit = 5) =>
    apiCall(`/dashboard/upcoming-events/?limit=${limit}`, "GET"),

  getDayEvents: (date) => apiCall(`/dashboard/day-events/?date=${date}`, "GET"),

  getStatusBreakdown: () => apiCall("/dashboard/status-breakdown/", "GET"),

  getTimeline: (days = 30) =>
    apiCall(`/dashboard/timeline/?days=${days}`, "GET"),

  getCategoryBreakdown: () => apiCall("/dashboard/category-breakdown/", "GET"),
};
